// frontend/src/utils/clientInstance.ts
//
// A marker that separates one browser profile from another.
//
// 🔒 WHY THIS EXISTS. The backend recognises a browser by `web | OS | browser |
// major` (see backend/src/core/sessions/request-shape.ts). Two different Windows
// laptops both running Chrome 141 hashed to ONE device, so the second machine
// was not refused by the one-login rule: claimSlot saw the same device, ended
// the first session as `signed_out_here`, raised no alert, and the first person
// was logged out with nothing to explain it. A phone does not have this problem
// — it reports a real make and model — so this is browsers only.
//
// 🔒 WHY IT IS SAFE WHERE X-Device-Id WAS NOT. The phone fingerprint deliberately
// ignores X-Device-Id, because a client-chosen id can be set to the value of the
// device currently holding the slot and impersonate it. The same risk is
// theoretically present here, and the next reader will ask about it. Two reasons
// it does not bite:
//   • To read someone's marker you must already be able to read that browser's
//     localStorage — at which point you are inside the browser whose slot you
//     would be stealing, and their cookie is right there next to it.
//   • The marker is only ever APPENDED to the hash input, so it can only make
//     MORE machines distinct, never fewer. A forged, cleared or absent marker
//     degrades to exactly the old behaviour. It can never merge two browsers
//     that are separate today, and it can never reach the phone's slot.
//
// 🔒 SPEC D4 says a device is never recognised by anything the user TYPES. This
// value is generated and stored, never typed and never shown, so it is inside
// D4. The cost, accepted explicitly by the owner: clearing browser data now
// looks like a new device, so new-device alerts become more common.
//
// 🔒 NO PRODUCT VOCABULARY (P3). This file copies to Sun Stellar verbatim.

const STORAGE_KEY = 'aura.clientInstance';

/** crypto.randomUUID where it exists (needs a secure context), else a
 *  good-enough random string. Uniqueness only has to hold across the machines
 *  of one tenant, and a collision degrades to today's behaviour, not to a
 *  wrong device. */
function generateId(): string {
  try {
    const c: Crypto | undefined = globalThis.crypto;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  } catch {
    // Fall through — some embedded webviews throw on `crypto` access.
  }
  const rand = () => Math.random().toString(36).slice(2, 10);
  return `${Date.now().toString(36)}-${rand()}-${rand()}`;
}

/**
 * The marker for this browser profile, or `null` when storage is unavailable.
 *
 * 🔒 EVERY storage touch is wrapped. Private-browsing modes and "block all
 * cookies" settings THROW on `localStorage` access — they do not politely
 * return null — and an uncaught throw here would run inside the axios request
 * interceptor and break login for everyone in that mode.
 *
 * 🔒 A failed WRITE returns null rather than the value we just generated. An
 * unstored marker would be different on every single request, which would make
 * every request look like a brand-new device — strictly worse than sending
 * nothing at all.
 */
export function getClientInstanceId(): string | null {
  // Deliberately not memoised: a cached value would outlive a cleared store and
  // would make the throwing-storage paths untestable in isolation. Two
  // localStorage reads per request is not a cost worth that.
  let existing: string | null = null;
  try {
    if (typeof window === 'undefined') return null;
    existing = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (existing) return existing;

  const generated = generateId();
  try {
    window.localStorage.setItem(STORAGE_KEY, generated);
  } catch {
    return null;
  }
  return generated;
}

/** Exported for the header name to have exactly one definition. */
export const CLIENT_INSTANCE_HEADER = 'X-Client-Instance';
